import React, { useMemo, useState } from "react";
import { Select, MenuItem, FormControl, InputLabel, Tooltip, Button } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SSHService from "../../../shared/hooks/SSHService";
import {
  PostureRow,
  isEndpointVendorName,
  ENDPOINT_TEMPLATE_INFO_MESSAGE,
  defaultPortForConnector,
} from "./NewScanDrawer";

interface MultiIpSshUploadProps {
  isMultiIpMode: boolean;
  setIsMultiIpMode: (val: boolean) => void;
  isSshUploadMode: boolean;
  setIsSshUploadMode: (val: boolean) => void;
  postureRows: PostureRow[];
  addPostureRow: (index?: number) => void;
  updatePostureRow: (id: string, field: keyof PostureRow, value: any) => void;
  removePostureRow: (id: string) => void;
  handlePostureRowKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, rowId: string, index: number) => void;
  renderCredentialForm: (isSubrow?: boolean, rowId?: string) => React.ReactNode;
  
  postureUploadFiles: File[];
  setPostureUploadFiles: React.Dispatch<React.SetStateAction<File[]>>;
  invalidFilesList: string[];
  setInvalidFilesList: React.Dispatch<React.SetStateAction<string[]>>;
  postureUploadError: string;
  setEventId: (id: string | null) => void;
  
  handlePostureUploadDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handlePostureUploadDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handlePostureChooseFilesClick: () => void;
  postureUploadInputRef: React.RefObject<HTMLInputElement>;
  handlePostureUploadInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  
  vendorsList: any[];
  credentials: any[];
  connectorType: string;
  isDarkMode: boolean;
  credentialId: string;
  setCredentialId: (val: string) => void;
  masterTemplates?: any[];
  onClearCsvImport?: () => void;
  applyBulkToAllRows?: (opts: {
    credentialId?: string;
    templateId?: string;
    port?: string;
  }) => void;
}

const MultiIpSshUpload: React.FC<MultiIpSshUploadProps> = ({
  isMultiIpMode,
  isSshUploadMode,
  postureRows,
  addPostureRow,
  updatePostureRow,
  removePostureRow,
  handlePostureRowKeyDown,
  postureUploadFiles,
  setPostureUploadFiles,
  invalidFilesList,
  setInvalidFilesList,
  postureUploadError,
  setEventId,
  handlePostureUploadDrop,
  handlePostureUploadDragOver,
  handlePostureChooseFilesClick,
  postureUploadInputRef,
  handlePostureUploadInputChange,
  vendorsList,
  credentials,
  connectorType,
  isDarkMode,
  renderCredentialForm,
  credentialId,
  setCredentialId,
  masterTemplates = [],
  onClearCsvImport,
  applyBulkToAllRows,
}) => {
  const showTemplateColumn =
    connectorType === "ssh" || connectorType === "api";

  const getVendorName = (vendorId: string | null) => {
    if (!vendorId) return "";
    const vendor = vendorsList.find((v: any) => String(v.id) === String(vendorId));
    return vendor?.name || "";
  };

  const getRowTemplateState = (row: PostureRow) => {
    const vendorName = getVendorName(row.vendorId);
    const isEndpoint = isEndpointVendorName(vendorName);
    const hasVendorOs = Boolean(row.vendorId && row.categoryId);
    const disabled = isEndpoint || !hasVendorOs;
    let infoMessage = "";
    if (isEndpoint) {
      infoMessage = ENDPOINT_TEMPLATE_INFO_MESSAGE;
    } else if (!hasVendorOs) {
      infoMessage =
        "Select Vendor and Category (OS) to enable master template selection. Leave unselected for no custom template.";
    }
    const options = (masterTemplates || []).filter((t: any) => {
      const tid = String(t.vendor_id || "");
      const mid = String(t.device_model_id || "");
      return (
        tid === String(row.vendorId || "") &&
        mid === String(row.categoryId || "")
      );
    });
    return { disabled, infoMessage, options, isEndpoint };
  };

  const selectSx = {
    height: 40,
    borderRadius: "8px",
    fontSize: 13,
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "#4a4a52" : "#D0D5DD",
    },
    "& .MuiSelect-select": {
      py: "8px !important",
      pr: "28px !important",
      fontSize: 13,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "block",
    },
  };

  const [bulkTemplateId, setBulkTemplateId] = useState("");
  const [bulkCredentialId, setBulkCredentialId] = useState("");
  const [bulkPort, setBulkPort] = useState("");

  const bulkTemplateOptions = useMemo(() => {
    const filled = postureRows.filter((r) => r.assetIp.trim().length > 0);
    const rowsForBulk = filled.length ? filled : postureRows;
    return (masterTemplates || []).filter((t: any) =>
      rowsForBulk.some((row) => {
        if (!row.vendorId || !row.categoryId) return false;
        const vendor = vendorsList.find(
          (v: any) => String(v.id) === String(row.vendorId)
        );
        if (isEndpointVendorName(vendor?.name)) return false;
        return (
          String(t.vendor_id) === String(row.vendorId) &&
          String(t.device_model_id) === String(row.categoryId)
        );
      })
    );
  }, [masterTemplates, postureRows, vendorsList]);

  const duplicateIpWarning = useMemo(() => {
    const map = new Map<string, number[]>();
    postureRows.forEach((row, idx) => {
      const ip = row.assetIp.trim();
      if (!ip) return;
      const list = map.get(ip) || [];
      list.push(idx + 1);
      map.set(ip, list);
    });
    const dups = [...map.entries()].filter(([, rows]) => rows.length > 1);
    if (!dups.length) return null;
    return dups
      .map(([ip, rows]) => {
        const labels = rows.map(String);
        const list =
          labels.length === 2
            ? `${labels[0]} and ${labels[1]}`
            : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
        return `${ip} appears in rows ${list}`;
      })
      .join(". ");
  }, [postureRows]);

  const duplicateIpSet = useMemo(() => {
    const counts = new Map<string, number>();
    postureRows.forEach((row) => {
      const ip = row.assetIp.trim();
      if (!ip) return;
      counts.set(ip, (counts.get(ip) || 0) + 1);
    });
    return new Set(
      [...counts.entries()].filter(([, n]) => n > 1).map(([ip]) => ip)
    );
  }, [postureRows]);

  const handleApplyBulk = () => {
    if (!applyBulkToAllRows) return;
    const portVal = String(bulkPort || "").trim();
    applyBulkToAllRows({
      ...(bulkCredentialId ? { credentialId: bulkCredentialId } : {}),
      ...(bulkTemplateId ? { templateId: bulkTemplateId } : {}),
      ...(portVal ? { port: portVal } : {}),
    });
  };

  const thLabel = (text: string, required?: boolean, tip?: string) => (
    <span className="nsd-multi-ip-th-label">
      {text}
      {required ? <span className="nsd-asset-required">*</span> : null}
      {tip ? (
        <Tooltip title={tip} arrow placement="top">
          <InfoOutlinedIcon className="nsd-multi-ip-th-info" />
        </Tooltip>
      ) : null}
    </span>
  );

  const hasImportedAssets = postureRows.some((r) => r.assetIp.trim().length > 0);
  const showAssetTable = !isSshUploadMode || hasImportedAssets;

  const clearCsvImport = () => {
    if (onClearCsvImport) {
      onClearCsvImport();
      return;
    }
    setPostureUploadFiles([]);
    setInvalidFilesList([]);
    setEventId(null);
  };

  const renderCsvImportCard = (compact?: boolean) => (
    <div
      className={`nsd-csv-upload-card${compact ? " nsd-csv-upload-card--compact" : ""}`}
      onDrop={handlePostureUploadDrop}
      onDragOver={handlePostureUploadDragOver}
      onClick={handlePostureChooseFilesClick}
      role="button"
      tabIndex={0}
    >
      <div className="nsd-csv-upload-inner">
        {!compact && (
          <div className="nsd-csv-upload-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ width: 28, height: 28, color: "#3A2AdA" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
        )}
        <div className="nsd-csv-upload-title">
          {compact ? "Replace CSV file" : "Upload CSV file"}
        </div>
        <div className="nsd-csv-upload-subtitle">
          Drag and drop your CSV file here or{" "}
          <span
            className="nsd-csv-upload-click"
            onClick={(e) => {
              e.stopPropagation();
              postureUploadInputRef.current?.click();
            }}
          >
            Choose file
          </span>
        </div>
        {!compact && (
          <div className="nsd-csv-upload-hint">(Supported file formats - CSV)</div>
        )}

        <button
          type="button"
          className="nsd-csv-upload-link"
          onClick={(e) => {
            e.stopPropagation();
            SSHService.fetchFileData("ssh");
          }}
        >
          Download sample file
        </button>

        <input
          ref={postureUploadInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handlePostureUploadInputChange}
        />
      </div>
      {postureUploadError && (
        <div className="nsd-upload-error">{postureUploadError}</div>
      )}
      {postureUploadFiles.length > 0 && (
        <div className="nsd-upload-files">
          <div className="nsd-upload-files-header">
            <span>
              Loaded from{" "}
              <strong>{postureUploadFiles[0]?.name}</strong>
              {hasImportedAssets
                ? ` · ${postureRows.filter((r) => r.assetIp.trim()).length} assets`
                : ""}
            </span>
            <button
              type="button"
              className="nsd-upload-files-clear"
              onClick={(e) => {
                e.stopPropagation();
                clearCsvImport();
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isMultiIpMode && (
        <div className="nsd-assets-section">
          {isSshUploadMode && renderCsvImportCard(hasImportedAssets)}

          {showAssetTable ? (
            <div className="nsd-multi-ip-table">
              {isSshUploadMode && applyBulkToAllRows && (
                <div className="nsd-apply-all-bar">
                  <div className="nsd-apply-all-copy">
                    <div className="nsd-apply-all-title">Apply to all rows</div>
                    <div className="nsd-apply-all-subtitle">
                      Set once, then override per row.
                    </div>
                  </div>
                  <div className="nsd-apply-all-fields">
                    {showTemplateColumn && (
                      <div className="nsd-apply-all-field">
                        <label>Template</label>
                        <Select
                          value={bulkTemplateId}
                          onChange={(e) =>
                            setBulkTemplateId(String(e.target.value || ""))
                          }
                          displayEmpty
                          size="small"
                          fullWidth
                          sx={selectSx}
                          renderValue={(val: any) => {
                            if (!val) return "Select";
                            const tpl = bulkTemplateOptions.find(
                              (t: any) =>
                                String(t.template_id || t.id) === String(val)
                            );
                            return tpl?.name || tpl?.file_name || String(val);
                          }}
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {bulkTemplateOptions.map((t: any) => {
                            const id = String(t.template_id || t.id);
                            return (
                              <MenuItem key={id} value={id}>
                                {t.name || t.file_name || id}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </div>
                    )}
                    <div className="nsd-apply-all-field">
                      <label>Credential</label>
                      <Select
                        value={bulkCredentialId}
                        onChange={(e) =>
                          setBulkCredentialId(String(e.target.value || ""))
                        }
                        displayEmpty
                        size="small"
                        fullWidth
                        sx={selectSx}
                        renderValue={(val: any) => {
                          if (!val) return "Select";
                          const cred = credentials.find((c) => c.id === val);
                          return cred?.name || String(val);
                        }}
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                        {credentials.map((cred) => (
                          <MenuItem key={cred.id} value={cred.id}>
                            {cred.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    <div className="nsd-apply-all-field nsd-apply-all-field--port">
                      <label>Port</label>
                      <input
                        type="text"
                        value={bulkPort}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            setBulkPort(e.target.value);
                          }
                        }}
                        placeholder={defaultPortForConnector(connectorType)}
                        className="nsd-multi-ip-input"
                      />
                    </div>
                    <div className="nsd-apply-all-field nsd-apply-all-field--action">
                      <label className="nsd-apply-all-action-spacer">&nbsp;</label>
                      <Button
                        type="button"
                        className="nsd-apply-all-btn"
                        onClick={handleApplyBulk}
                        disabled={
                          !bulkCredentialId &&
                          !bulkTemplateId &&
                          !String(bulkPort || "").trim()
                        }
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {duplicateIpWarning && (
                <div className="nsd-dup-ip-banner" role="alert">
                  <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
                  <span>
                    {duplicateIpWarning}. Each asset needs a unique IP or CIDR.
                  </span>
                </div>
              )}

              <div
                className={`nsd-multi-ip-table-header${
                  showTemplateColumn ? "" : " nsd-multi-ip-table-header--no-template"
                }`}
              >
                <div className="nsd-multi-ip-th nsd-multi-ip-th-num">#</div>
                <div className="nsd-multi-ip-th nsd-multi-ip-th-ip">
                  {thLabel(
                    "IP / CIDR",
                    true,
                    "Enter a single IP (e.g. 192.168.1.1) or a CIDR range (e.g. 10.0.0.0/24)."
                  )}
                </div>
                <div className="nsd-multi-ip-th nsd-multi-ip-th-vendor">
                  {thLabel("Vendor")}
                </div>
                <div className="nsd-multi-ip-th nsd-multi-ip-th-category">
                  {thLabel("Category")}
                </div>
                {showTemplateColumn && (
                  <div className="nsd-multi-ip-th nsd-multi-ip-th-template">
                    {thLabel(
                      "Template",
                      false,
                      "Select Vendor and Category (OS) to enable master template selection. Leave unselected for no custom template."
                    )}
                  </div>
                )}
                <div className="nsd-multi-ip-th nsd-multi-ip-th-credential">
                  {thLabel("Credential", true)}
                </div>
                <div className="nsd-multi-ip-th nsd-multi-ip-th-port">
                  {thLabel(
                    "Port",
                    false,
                    `Default port is ${defaultPortForConnector(
                      connectorType
                    )}. Leave blank to use the default, or enter a custom port.`
                  )}
                </div>
                <div className="nsd-multi-ip-th nsd-multi-ip-th-action" />
              </div>

              <div className="nsd-multi-ip-rows">
                {postureRows.map((row, index) => {
                  const templateState = showTemplateColumn
                    ? getRowTemplateState(row)
                    : null;
                  return (
                    <React.Fragment key={row.id}>
                      <div
                        className={`nsd-multi-ip-row${
                          showTemplateColumn
                            ? ""
                            : " nsd-multi-ip-row--no-template"
                        }`}
                      >
                        <div className="nsd-multi-ip-row-num">{index + 1}</div>

                        <div className="nsd-multi-ip-cell-ip">
                          <input
                            type="text"
                            value={row.assetIp}
                            onChange={(e) =>
                              updatePostureRow(row.id, "assetIp", e.target.value)
                            }
                            onKeyDown={(e) =>
                              handlePostureRowKeyDown(e, row.id, index)
                            }
                            placeholder="e.g. 192.168.1.1 or 10.0.0.0/24"
                            className={`nsd-multi-ip-input${
                              row.assetIp.trim() &&
                              duplicateIpSet.has(row.assetIp.trim())
                                ? " nsd-multi-ip-input--dup"
                                : ""
                            }`}
                          />
                        </div>

                        <div className="nsd-multi-ip-cell-vendor">
                          <Select
                            value={row.vendorId || ""}
                            onChange={(e) =>
                              updatePostureRow(row.id, "vendorId", e.target.value)
                            }
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={selectSx}
                            renderValue={(val: any) => {
                              if (!val) return "Select";
                              const v = vendorsList.find(
                                (item: any) => String(item.id) === String(val)
                              );
                              return v?.name || String(val);
                            }}
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            {vendorsList.map((v: any) => {
                              const vName = v.name.toLowerCase();
                              if (
                                connectorType === "api" &&
                                (vName === "paloalto" ||
                                  vName === "palo alto" ||
                                  vName === "fortinet" ||
                                  vName === "forti" ||
                                  vName === "fortigate" ||
                                  vName === "sophos")
                              ) {
                                return (
                                  <MenuItem key={v.id} value={v.id}>
                                    {v.name}
                                  </MenuItem>
                                );
                              }
                              if (
                                connectorType === "ssh" &&
                                vName !== "microsoft"
                              ) {
                                return (
                                  <MenuItem key={v.id} value={v.id}>
                                    {v.name}
                                  </MenuItem>
                                );
                              }
                              if (
                                connectorType === "windows" &&
                                vName === "microsoft"
                              ) {
                                return (
                                  <MenuItem key={v.id} value={v.id}>
                                    {v.name}
                                  </MenuItem>
                                );
                              }
                              return null;
                            })}
                          </Select>
                        </div>

                        <div className="nsd-multi-ip-cell-category">
                          <Select
                            value={row.categoryId || ""}
                            onChange={(e) =>
                              updatePostureRow(
                                row.id,
                                "categoryId",
                                e.target.value
                              )
                            }
                            disabled={!row.vendorId}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={selectSx}
                            renderValue={(val: any) => {
                              if (!val) return "Select";
                              const cat = row.categories.find(
                                (c: any) => String(c.id) === String(val)
                              );
                              return cat?.name || String(val);
                            }}
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            {row.categories.map((c: any) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>

                        {showTemplateColumn && templateState && (
                          <div className="nsd-multi-ip-cell-template">
                            <Select
                              value={row.templateId || ""}
                              onChange={(e) =>
                                updatePostureRow(
                                  row.id,
                                  "templateId",
                                  e.target.value || null
                                )
                              }
                              disabled={templateState.disabled}
                              displayEmpty
                              size="small"
                              fullWidth
                              sx={selectSx}
                              renderValue={(val: any) => {
                                if (!val) return "Select";
                                const tpl = templateState.options.find(
                                  (t: any) =>
                                    String(t.template_id || t.id) ===
                                    String(val)
                                );
                                return (
                                  tpl?.name || tpl?.file_name || String(val)
                                );
                              }}
                            >
                              <MenuItem value="">
                                <em>Select</em>
                              </MenuItem>
                              {templateState.options.map((t: any) => {
                                const id = String(t.template_id || t.id);
                                return (
                                  <MenuItem key={id} value={id}>
                                    {t.name || t.file_name || id}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </div>
                        )}

                        <div className="nsd-multi-ip-cell-credential">
                          <Select
                            value={row.credentialId}
                            onChange={(e) =>
                              updatePostureRow(
                                row.id,
                                "credentialId",
                                e.target.value
                              )
                            }
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={selectSx}
                            renderValue={(val: any) => {
                              if (!val) return "Select";
                              if (val === "__create_new__")
                                return "Create New Credential";
                              const cred = credentials.find((c) => c.id === val);
                              return cred?.name || String(val);
                            }}
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            <MenuItem
                              value="__create_new__"
                              sx={{
                                borderBottom: `1px solid ${
                                  isDarkMode ? "#4A4A52" : "#C0C5CE"
                                }`,
                                mb: 0.5,
                                pb: 1,
                                pt: 1,
                              }}
                            >
                              <AddCircleOutlineIcon
                                sx={{ color: "#3a2ada", fontSize: 18, mr: 1 }}
                              />
                              <span
                                style={{
                                  color: "#3a2ada",
                                  fontWeight: 600,
                                  fontSize: 13,
                                }}
                              >
                                Create New Credential
                              </span>
                            </MenuItem>
                            {credentials.map((cred) => (
                              <MenuItem key={cred.id} value={cred.id}>
                                {cred.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>

                        <div className="nsd-multi-ip-cell-port">
                          <input
                            type="text"
                            value={row.port || ""}
                            onChange={(e) => {
                              if (/^\d*$/.test(e.target.value)) {
                                updatePostureRow(row.id, "port", e.target.value);
                              }
                            }}
                            placeholder={defaultPortForConnector(connectorType)}
                            className="nsd-multi-ip-input nsd-multi-ip-input--port"
                          />
                        </div>

                        <div className="nsd-multi-ip-cell-action">
                          <button
                            type="button"
                            onClick={() => removePostureRow(row.id)}
                            className="nsd-multi-ip-delete-btn"
                            aria-label="Delete asset"
                            disabled={postureRows.length <= 1}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </button>
                        </div>
                      </div>
                      {row.credentialId === "__create_new__" &&
                        renderCredentialForm(true, row.id)}
                    </React.Fragment>
                  );
                })}
              </div>

              <button
                type="button"
                className="nsd-add-asset-btn nsd-add-asset-btn--compact"
                onClick={() => addPostureRow(postureRows.length - 1)}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Add another asset
              </button>

              <div className="nsd-asset-required-note">
                <span className="nsd-asset-required">*</span> Required. Fields
                without an asterisk are optional.
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
};

export default MultiIpSshUpload;
